/**
 * Describes the read-only detail page for each listing type.
 *
 * ListingView renders these declaratively, so the four pages stay consistent
 * and a new field is one line here rather than a new component.
 *
 * Section kinds:
 *   fields - label/value grid; each entry is a `path` or a `value(record)` fn
 *   text   - a single free-text paragraph at `path`
 *   chips  - groups of pills (amenities, inclusions, ...)
 *   cards  - a sub-collection at `path` (room types, services, variants, days)
 *
 * Empty values, empty groups and empty sections hide themselves.
 */

const joinCity = (record) =>
  [record.city, record.state, record.country].filter(Boolean).join(', ');

const cheapest = (items, key) => {
  const prices = (items || []).map((item) => Number(item[key])).filter((n) => !Number.isNaN(n) && n > 0);
  return prices.length ? Math.min(...prices) : null;
};

/** Contact block shared by all four types. */
const contactSection = {
  kind: 'fields',
  title: 'Contact',
  fields: [
    { label: 'WhatsApp', path: 'whatsappNumber' },
    { label: 'Phone', path: 'phone' },
    { label: 'Alternate mobile', path: 'alternatePhone' },
    { label: 'Email', path: 'email' },
    { label: 'Website', path: 'website' },
    { label: 'Google listing', path: 'googleListingLink' },
  ],
};

/** Location block shared by all four types. */
const locationSection = {
  kind: 'fields',
  title: 'Location',
  fields: [
    { label: 'Address', path: 'address' },
    { label: 'Display address', path: 'displayAddress' },
    { label: 'City', path: 'city' },
    { label: 'State', path: 'state' },
    { label: 'Country', path: 'country' },
    { label: 'Zip code', path: 'zipCode' },
    {
      label: 'Coordinates',
      value: (r) => (r.latitude && r.longitude ? `${r.latitude}, ${r.longitude}` : null),
    },
  ],
};

const socialSection = {
  kind: 'fields',
  title: 'Social links',
  fields: [
    { label: 'Facebook', path: 'socialLinks.facebook' },
    { label: 'Instagram', path: 'socialLinks.instagram' },
    { label: 'YouTube', path: 'socialLinks.youtube' },
  ],
};

const LISTING_VIEWS = {
  hotel: {
    endpoint: 'hotels',
    permission: 'hotels',
    label: 'Hotels',
    singular: 'hotel',
    subtitle: (r) => r.category?.name,
    facts: (r, { money }) => [
      { label: 'City', value: joinCity(r) },
      { label: 'From', value: money(cheapest(r.roomTypes, 'pricePerNight')) },
      { label: 'Room types', value: r.roomTypes?.length || null },
      { label: 'Total rooms', value: r.totalRooms || null },
      { label: 'Star rating', value: r.starRating ? `${r.starRating}★` : null },
      { label: 'Check in / out', value: r.checkInTime ? `${r.checkInTime} – ${r.checkOutTime}` : null },
      { label: 'Hourly stay', value: r.hourlyStay?.enabled ? 'Enabled' : null },
    ],
    sections: [
      { kind: 'text', title: 'Short description', path: 'shortDescription' },
      { kind: 'text', title: 'Description', path: 'description' },
      contactSection,
      locationSection,
      {
        kind: 'fields',
        title: 'Hourly stay',
        fields: [
          { label: 'Status', value: (r) => (r.hourlyStay?.enabled ? 'Enabled' : 'Not enabled') },
          { label: 'Check-in window', value: (r) => (r.hourlyStay?.enabled ? `${r.hourlyStay.checkInFrom} – ${r.hourlyStay.checkInTo}` : null) },
          { label: 'Durations', value: (r) => (r.hourlyStay?.enabled ? (r.hourlyStay.durations || []).map((h) => `${h} hrs`).join(', ') : null) },
        ],
      },
      {
        kind: 'cards',
        title: 'Room types',
        path: 'roomTypes',
        unit: 'room type',
        item: {
          title: (i) => i.roomType,
          price: (i) => i.pricePerNight,
          cutPrice: (i) => i.cutPrice,
          priceSuffix: '/ night',
          meta: (i) => [
            { label: 'Sleeps', value: i.maxPersons },
            { label: 'Bed', value: i.bedType },
            { label: 'Size', value: i.roomSize },
            ...[3, 6, 9, 12].map((h) => ({
              label: `${h} hrs`,
              value: i.hourlyRates?.[`h${h}`]?.price != null ? `₹${i.hourlyRates[`h${h}`].price}` : null,
            })),
          ],
          chips: (i) => [
            { label: 'Room amenities', items: i.roomAmenities },
            { label: 'Additional', items: i.additionalAmenities },
          ],
        },
      },
      {
        kind: 'chips',
        title: 'Amenities & facilities',
        groups: (r) => [
          { label: 'Facilities', items: r.facilities },
          { label: 'Amenities', items: r.amenities },
          { label: 'Languages', items: r.languages },
        ],
      },
      {
        kind: 'fields',
        title: 'Policies',
        fields: [
          { label: 'Cancellation', path: 'cancellationPolicy' },
          { label: 'Payment', path: 'paymentPolicy' },
          { label: 'Pets', path: 'petPolicy' },
          { label: 'Smoking', path: 'smokingPolicy' },
          { label: 'Age restriction', path: 'ageRestriction' },
        ],
      },
      {
        kind: 'cards',
        title: 'Nearby attractions',
        path: 'nearbyAttractions',
        unit: 'attraction',
        item: {
          title: (i) => i.name,
          meta: (i) => [{ label: 'Distance', value: i.distance }],
        },
      },
      socialSection,
    ],
  },

  spa: {
    endpoint: 'spas',
    permission: 'spas',
    label: 'Spas',
    singular: 'spa',
    facts: (r, { money }) => [
      { label: 'City', value: joinCity(r) },
      { label: 'From', value: money(cheapest(r.services, 'price')) },
      { label: 'Services', value: r.services?.length || null },
      { label: 'Rating', value: r.rating ? `${r.rating}★` : null },
    ],
    sections: [
      { kind: 'text', title: 'Short description', path: 'shortDescription' },
      { kind: 'text', title: 'Description', path: 'description' },
      contactSection,
      locationSection,
      {
        kind: 'cards',
        title: 'Services',
        path: 'services',
        unit: 'service',
        item: {
          title: (i) => i.serviceName,
          price: (i) => i.price,
          cutPrice: (i) => i.cutPrice,
          meta: (i) => [{ label: 'Duration', value: i.duration }],
          chips: (i) => [
            { label: 'Service amenities', items: i.serviceAmenities },
            { label: 'Additional', items: i.additionalAmenities },
          ],
        },
      },
      {
        kind: 'chips',
        title: 'Amenities & facilities',
        groups: (r) => [
          { label: 'Facilities', items: r.facilities },
          { label: 'Amenities', items: r.amenities },
          { label: 'Languages', items: r.languages },
        ],
      },
      {
        kind: 'fields',
        title: 'Policies',
        fields: [
          { label: 'Cancellation', path: 'cancellationPolicy' },
          { label: 'Booking', path: 'bookingPolicy' },
          { label: 'Age restriction', path: 'ageRestriction' },
        ],
      },
      {
        kind: 'cards',
        title: 'Nearby attractions',
        path: 'nearbyAttractions',
        unit: 'attraction',
        item: {
          title: (i) => i.name,
          meta: (i) => [{ label: 'Distance', value: i.distance }],
        },
      },
      socialSection,
    ],
  },

  tour: {
    endpoint: 'tours',
    permission: 'tours',
    label: 'Tours & Activities',
    singular: 'tour',
    subtitle: (r) => r.agencyName,
    facts: (r, { money }) => [
      { label: 'City', value: joinCity(r) },
      { label: 'From', value: money(cheapest(r.activityOptions, 'pricePerPerson')) },
      { label: 'Options', value: r.activityOptions?.length || null },
      { label: 'Duration', value: r.duration },
      { label: 'Suitable for', value: r.suitableFor },
      { label: 'Availability', value: r.availableDatesType },
    ],
    sections: [
      { kind: 'text', title: 'Short description', path: 'shortDescription' },
      { kind: 'text', title: 'Description', path: 'description' },
      contactSection,
      locationSection,
      {
        kind: 'cards',
        title: 'Activity options',
        path: 'activityOptions',
        unit: 'option',
        item: {
          title: (i) => i.optionName,
          price: (i) => i.pricePerPerson,
          cutPrice: (i) => i.cutPrice,
          priceSuffix: '/ person',
          meta: (i) => [{ label: 'Cancellation', value: i.cancellationPolicyTag }],
        },
      },
      {
        kind: 'chips',
        title: 'Schedule',
        groups: (r) => [
          { label: 'Time slots', items: r.timeOptions },
          { label: 'Specific dates', items: (r.specificDates || []).map((d) => new Date(d).toLocaleDateString('en-IN')) },
        ],
      },
      {
        kind: 'fields',
        title: 'Good to know',
        fields: [
          { label: 'Important info', path: 'importantInfo' },
          { label: 'Other info', path: 'otherInfo' },
        ],
      },
    ],
  },

  travelPackage: {
    endpoint: 'travel-packages',
    permission: 'travel-packages',
    label: 'Travel Packages',
    singular: 'travel package',
    subtitle: (r) => r.agencyName,
    facts: (r, { money }) => [
      { label: 'City', value: joinCity(r) },
      { label: 'From', value: money(cheapest(r.packageVariants, 'pricePerPerson')) },
      { label: 'Variants', value: r.packageVariants?.length || null },
      {
        label: 'Duration',
        value: r.numberOfNights ? `${r.numberOfNights}N / ${r.numberOfDays}D` : null,
      },
      { label: 'Departs from', value: r.departureCity },
      { label: 'Itinerary', value: r.itinerary?.length ? `${r.itinerary.length} days` : null },
    ],
    sections: [
      { kind: 'text', title: 'Short description', path: 'shortDescription' },
      { kind: 'text', title: 'Description', path: 'description' },
      contactSection,
      locationSection,
      {
        kind: 'cards',
        title: 'Package variants',
        path: 'packageVariants',
        unit: 'variant',
        item: {
          title: (i) => i.variantName,
          price: (i) => i.pricePerPerson,
          cutPrice: (i) => i.cutPrice,
          priceSuffix: '/ person',
          meta: (i) => [{ label: 'Cancellation', value: i.cancellationPolicyTag }],
        },
      },
      {
        kind: 'cards',
        title: 'Itinerary',
        path: 'itinerary',
        unit: 'day',
        item: {
          title: (i) => `Day ${i.dayNumber}${i.title ? ` · ${i.title}` : ''}`,
          chips: (i) => [{ label: 'Activities', items: i.activities }],
        },
      },
      {
        kind: 'chips',
        title: 'Inclusions & exclusions',
        groups: (r) => [
          { label: 'Included', items: r.inclusions },
          { label: 'Not included', items: r.exclusions },
          { label: 'Languages', items: r.languages },
        ],
      },
      {
        kind: 'fields',
        title: 'Good to know',
        fields: [
          { label: 'Cancellation', path: 'cancellationPolicy' },
          { label: 'Important info', path: 'importantInfo' },
          { label: 'Other info', path: 'otherInfo' },
        ],
      },
      socialSection,
    ],
  },
};

export default LISTING_VIEWS;
